/**
 * Brain / knowledge-base overview from GET /api/brain/overview.
 */
import { useCallback, useEffect, useState } from "react";
import { brainApi, healthApi } from "@/lib/api";

const EMPTY_OVERVIEW = {
  kpis: { documents: 0, chunks: 0, embeddings: 0, vector_size_mb: 0, last_sync: null },
  knowledgeSources: [],
  customSources: [],
  connected_count: 0,
  document_count: 0,
  has_knowledge: false,
};

function normalizeOverview(data) {
  if (!data || typeof data !== "object") return EMPTY_OVERVIEW;
  const kpis = data.kpis && typeof data.kpis === "object" ? data.kpis : EMPTY_OVERVIEW.kpis;
  const raw = data.knowledge_sources ?? data.sources;
  const knowledgeSources = (Array.isArray(raw) ? raw : []).filter(
    (s, i, arr) => s.id !== "file_upload" || !arr.some((x) => x.id === "custom"),
  );
  return {
    kpis: {
      documents: Number(kpis.documents) || 0,
      chunks: Number(kpis.chunks) || 0,
      embeddings: Number(kpis.embeddings) || 0,
      vector_size_mb: Number(kpis.vector_size_mb) || 0,
      last_sync: kpis.last_sync ?? null,
    },
    knowledgeSources,
    customSources: Array.isArray(data.custom_sources) ? data.custom_sources : [],
    connected_count: Number(data.connected_count) || 0,
    document_count: Number(data.document_count) || 0,
    has_knowledge: Boolean(data.has_knowledge),
  };
}

function brainErrorMessage(err) {
  if (err?.status === 404) {
    return "Brain API not found (HTTP 404). Restart the backend so /api/brain/overview is registered.";
  }
  if (err?.status === 401 || err?.status === 403) {
    return "Sign in again to load Brain status.";
  }
  return err?.message || "Couldn't load Brain status";
}

function logBrain(event, detail) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.info(`[brain] ${event}`, detail ?? "");
  }
}

export function useBrainOverview() {
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiIncomplete, setApiIncomplete] = useState(false);

  const checkBrainCapabilities = useCallback(async (isCancelled = () => false) => {
    try {
      const health = await healthApi.check();
      if (isCancelled()) return true;
      const brain = health?.brain;
      const ok = brain?.custom_sources === true && Number(brain?.api_version) >= 2;
      if (!isCancelled()) setApiIncomplete(!ok);
      if (!ok) {
        logBrain("Brain API incomplete — stale backend suspected", { brain });
      }
      return ok;
    } catch (e) {
      logBrain("Health check failed", { message: e?.message });
      if (!isCancelled()) setApiIncomplete(false);
      return true;
    }
  }, []);

  const refresh = useCallback(async ({ silent = false, isCancelled = () => false } = {}) => {
    logBrain("Brain API request started", { endpoint: "/api/brain/overview", silent });
    if (!silent && !isCancelled()) setLoading(true);
    try {
      if (!silent) await checkBrainCapabilities(isCancelled);
      if (isCancelled()) return undefined;
      const data = await brainApi.overview();
      if (isCancelled()) return undefined;
      const normalized = normalizeOverview(data);
      logBrain("Brain API response returned", {
        connected_count: normalized.connected_count,
        knowledge_source_count: normalized.knowledgeSources.length,
        custom_source_count: normalized.customSources.length,
      });
      if (!isCancelled()) {
        setOverview(normalized);
        setError(null);
      }
      return normalized;
    } catch (e) {
      logBrain("Brain API request failed", {
        endpoint: "/api/brain/overview",
        status: e?.status,
        message: e?.message,
      });
      if (!silent && !isCancelled()) {
        setError(brainErrorMessage(e));
        setOverview(EMPTY_OVERVIEW);
      }
      throw e;
    } finally {
      if (!silent && !isCancelled()) setLoading(false);
    }
  }, [checkBrainCapabilities]);

  useEffect(() => {
    logBrain("Brain page loaded");
    let cancelled = false;
    const isCancelled = () => cancelled;
    (async () => {
      try {
        await refresh({ isCancelled });
      } catch {
        // error state set in refresh()
      }
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const retry = useCallback(async (opts) => {
    try {
      await refresh(opts);
    } catch {
      // surfaced via error state
    }
  }, [refresh]);

  return { overview, loading, error, apiIncomplete, refresh: retry };
}

export default useBrainOverview;
